import React from 'react';
import { 
  Box, 
  Typography, 
  Container, 
  Paper, 
  useTheme,
  Stack
} from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import LanguageIcon from '@mui/icons-material/Language';
import SpeedIcon from '@mui/icons-material/Speed';
import SecurityIcon from '@mui/icons-material/Security';

interface FeatureCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ icon, title, description }) => {
  const theme = useTheme();
  
  return (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 2,
        transition: 'transform 0.3s, box-shadow 0.3s',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: '0 10px 20px rgba(0,0,0,0.1)',
        },
        background: theme.palette.background.paper,
      }}
    >
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          mb: 2,
          color: theme.palette.primary.main
        }}
      >
        {icon}
        <Typography variant="h5" component="h3" sx={{ ml: 1, fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>
      <Typography variant="body1" color="textSecondary">
        {description}
      </Typography>
    </Paper>
  );
};

const Features: React.FC = () => {
  const theme = useTheme();
  
  const features = [
    {
      icon: <CodeIcon fontSize="large" />,
      title: "Package Your Code",
      description: "Easily package your static frontend files and backend startup files into a single executable."
    },
    {
      icon: <LanguageIcon fontSize="large" />,
      title: "Browser Access",
      description: "Access your application through a browser interface, making it user-friendly and accessible."
    },
    {
      icon: <SpeedIcon fontSize="large" />,
      title: "Fast Deployment",
      description: "Streamline deployment with a single executable file that contains everything needed to run your application."
    },
    {
      icon: <SecurityIcon fontSize="large" />,
      title: "Secure Distribution",
      description: "Distribute your application securely without exposing your source code or configuration files."
    }
  ];
  
  return (
    <Box 
      id="features" 
      sx={{ 
        py: 8,
        background: theme.palette.mode === 'dark' 
          ? theme.palette.background.default
          : '#f9f9f9'
      }}
    >
      <Container maxWidth="lg">
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography 
            variant="h3" 
            component="h2" 
            gutterBottom
            sx={{ fontWeight: 'bold' }}
          >
            Features
          </Typography>
          <Typography 
            variant="h6" 
            color="textSecondary" 
            sx={{ maxWidth: 700, mx: 'auto' }}
          >
            Web Starter provides powerful tools to package and distribute your web applications efficiently.
          </Typography>
        </Box>
        
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          spacing={3} 
          flexWrap="wrap"
          sx={{
            '& > *': {
              width: { xs: '100%', sm: 'calc(50% - 12px)', md: 'calc(25% - 18px)' }
            }
          }}
        >
          {features.map((feature, index) => (
            <FeatureCard 
              key={index}
              icon={feature.icon} 
              title={feature.title} 
              description={feature.description} 
            />
          ))}
        </Stack>
      </Container>
    </Box>
  );
};

export default Features;
